import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * Tabla remota del panel (reemplazo de `material-table`, que quedó sin mantenimiento
 * y arrastraba @material-ui/core v4 — incompatible con React 18).
 *
 * Mantiene a propósito la misma API que ya usaban las pantallas, así el swap es sólo
 * un cambio de import:
 *
 *   - `columns`: [{ title, field, render(row), cellStyle, sorting, hidden }]
 *     Extras propios para la vista mobile (ver abajo): `mobileTitle` marca la columna
 *     que encabeza la card, `mobile: false` deja la columna afuera de la card, y
 *     `mobileBloque: true` la dibuja a lo ancho (para textos largos).
 *
 * En pantallas chicas (<= 48em) la tabla NO scrollea a lo ancho: cada fila se dibuja
 * como una card con lo importante (encabezado + pares etiqueta/valor) y el resto queda
 * detrás del boton "Ver detalle" (el mismo `detailPanel` de la vista escritorio).
 * Como en la card no hay headers donde clickear, el orden se elige con un select.
 *   - `data`: función `(query) => Promise<{ data, page, totalCount }>` — `query` trae
 *     `{ page, pageSize, search, orderBy: { field }, orderDirection }`, que es lo que
 *     espera `prepareDataSearch`. También acepta un array plano.
 *   - `options`: { search, sorting, paging, pageSize, pageSizeOptions }
 *   - `detailPanel`: `(row) => node` — fila expandible.
 *   - `tableRef`: expone `onQueryChange()` y `dataManager.changePageSize(n)`.
 *   - `localization`: mismas claves que material-table (pagination/toolbar/body).
 */

const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];

/** Igual que el breakpoint de las cards en custom.css: los dos tienen que moverse juntos. */
const MOBILE_QUERY = "(max-width: 48em)";

/** `true` mientras el viewport entre en el breakpoint mobile. */
const useEsMobile = () => {
  const soportado =
    typeof window !== "undefined" && typeof window.matchMedia === "function";

  const [esMobile, setEsMobile] = useState(
    () => soportado && window.matchMedia(MOBILE_QUERY).matches
  );

  useEffect(() => {
    if (!soportado) return undefined;

    const mql = window.matchMedia(MOBILE_QUERY);
    const alCambiar = (e) => setEsMobile(e.matches);

    setEsMobile(mql.matches);

    // Safari < 14 sólo tiene addListener sobre MediaQueryList.
    if (mql.addEventListener) mql.addEventListener("change", alCambiar);
    else mql.addListener(alCambiar);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", alCambiar);
      else mql.removeListener(alCambiar);
    };
  }, [soportado]);

  return esMobile;
};

/** Resuelve un path con puntos ("marca.nombre") contra la fila. */
const getByPath = (row, path) => {
  if (!path) return undefined;
  return path
    .split(".")
    .reduce(
      (acc, key) => (acc === undefined || acc === null ? acc : acc[key]),
      row
    );
};

const textoDe = (row, col) => {
  const value = getByPath(row, col.field);
  return value === undefined || value === null ? "" : value;
};

const MiTabla = ({
  title,
  columns = [],
  data,
  options = {},
  style,
  detailPanel,
  localization = {},
  tableRef,
  isLoading: isLoadingProp,
  onRowClick,
}) => {
  const {
    search: searchEnabled = true,
    sorting: sortingEnabled = true,
    paging = true,
    pageSize: initialPageSize = 10,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  } = options;

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [orderBy, setOrderBy] = useState(undefined);
  const [orderDirection, setOrderDirection] = useState("");
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandidas, setExpandidas] = useState({});
  const [reloadToken, setReloadToken] = useState(0);

  // `data` casi siempre llega como arrow inline: si entrara en las dependencias del
  // efecto, cada render dispararía una consulta nueva.
  const dataRef = useRef(data);
  dataRef.current = data;

  const esMobile = useEsMobile();

  const columnasVisibles = useMemo(
    () => columns.filter((col) => !col.hidden),
    [columns]
  );

  // Reparto de columnas para la card: las que no tienen `title` son los botones de
  // accion (van arriba a la derecha), una puede marcarse como encabezado con
  // `mobileTitle`, y `mobile: false` manda la columna al detalle.
  const { colTitulo, columnasDato, columnasAccion } = useMemo(() => {
    const enCard = columnasVisibles.filter((col) => col.mobile !== false);
    const acciones = enCard.filter((col) => !col.title);
    const conTitulo = enCard.filter((col) => !!col.title);
    const titulo =
      conTitulo.find((col) => col.mobileTitle) || conTitulo[0] || undefined;

    return {
      colTitulo: titulo,
      columnasDato: conTitulo.filter((col) => col !== titulo),
      columnasAccion: acciones,
    };
  }, [columnasVisibles]);

  const columnasOrdenables = useMemo(
    () =>
      sortingEnabled
        ? columnasVisibles.filter((col) => col.sorting !== false && !!col.field)
        : [],
    [columnasVisibles, sortingEnabled]
  );

  const pageCount = paging ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1;

  const fetchData = useCallback(() => {
    const source = dataRef.current;

    if (typeof source !== "function") {
      const todas = Array.isArray(source) ? source : [];
      setRows(todas);
      setTotalCount(todas.length);
      return undefined;
    }

    const query = {
      page,
      pageSize,
      search,
      orderBy: orderBy ? { field: orderBy } : undefined,
      orderDirection,
    };

    setLoading(true);
    let cancelado = false;

    Promise.resolve(source(query))
      .then((result) => {
        if (cancelado) return;
        setRows(result && result.data ? result.data : []);
        setTotalCount(result && result.totalCount ? result.totalCount : 0);
        // La pantalla puede forzar page=0 mutando el query (búsqueda nueva).
        if (result && typeof result.page === "number" && result.page !== page) {
          setPage(result.page);
        }
        setExpandidas({});
      })
      .catch(() => {
        if (!cancelado) setRows([]);
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });

    return () => {
      cancelado = true;
    };
  }, [page, pageSize, search, orderBy, orderDirection]);

  useEffect(() => fetchData(), [fetchData, reloadToken]);

  // Debounce del buscador: sin esto cada tecla es un POST.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch((prev) => {
        if (prev === searchInput) return prev;
        setPage(0);
        return searchInput;
      });
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useImperativeHandle(
    tableRef,
    () => ({
      onQueryChange: () => setReloadToken((n) => n + 1),
      dataManager: {
        changePageSize: (nuevo) =>
          setPageSize((actual) => (actual === nuevo ? actual : nuevo)),
      },
    }),
    []
  );

  const ordenarPor = (col) => {
    if (!sortingEnabled || col.sorting === false || !col.field) return;

    if (orderBy !== col.field) {
      setOrderBy(col.field);
      setOrderDirection("asc");
    } else if (orderDirection === "asc") {
      setOrderDirection("desc");
    } else {
      setOrderBy(undefined);
      setOrderDirection("");
    }
    setPage(0);
  };

  const toggleDetalle = (idx) =>
    setExpandidas((prev) => ({ ...prev, [idx]: !prev[idx] }));

  const desde = totalCount === 0 ? 0 : page * pageSize + 1;
  const hasta = Math.min((page + 1) * pageSize, totalCount);
  const etiquetaFilas =
    (localization.pagination && localization.pagination.labelRowsPerPage) ||
    "Filas por página";
  const vacio =
    (localization.body && localization.body.emptyDataSourceMessage) ||
    "No hay nada para mostrar";
  const placeholderBuscar =
    (localization.toolbar && localization.toolbar.searchPlaceholder) ||
    "Buscar...";

  const colSpan = columnasVisibles.length + (detailPanel ? 1 : 0);
  const cargando = isLoadingProp === undefined ? loading : isLoadingProp;

  const celda = (row, col) => (col.render ? col.render(row) : textoDe(row, col));

  const ordenMobile = esMobile && columnasOrdenables.length > 0;

  // Las pantallas pasan borde/redondeo por `style`; en modo card el marco lo pone cada
  // card, y un recuadro alrededor de todo quedaría doble.
  const estiloRaiz = esMobile
    ? {
        ...style,
        border: undefined,
        borderRadius: undefined,
        boxShadow: undefined,
      }
    : style;

  return (
    <div
      className={esMobile ? "mitabla mitabla--cards" : "mitabla"}
      style={estiloRaiz}
    >
      {(title || searchEnabled || ordenMobile) && (
        <div className="mitabla__toolbar">
          {title ? <h5 className="mitabla__title">{title}</h5> : <span />}
          {searchEnabled && (
            <div className="mitabla__search">
              <FontAwesomeIcon icon="fa-solid fa-magnifying-glass" />
              <input
                type="search"
                value={searchInput}
                placeholder={placeholderBuscar}
                aria-label={placeholderBuscar}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          )}

          {/* En card no hay headers para clickear: el orden se elige acá. */}
          {ordenMobile && (
            <div className="mitabla__orden">
              <label className="mitabla__orden-campo">
                <span>Ordenar por</span>
                <select
                  value={orderBy || ""}
                  onChange={(e) => {
                    const campo = e.target.value;
                    setOrderBy(campo || undefined);
                    setOrderDirection(campo ? "asc" : "");
                    setPage(0);
                  }}
                >
                  <option value="">Por defecto</option>
                  {columnasOrdenables.map((col) => (
                    <option key={col.field} value={col.field}>
                      {col.title}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="mitabla__orden-dir"
                disabled={!orderBy}
                aria-label={
                  orderDirection === "desc"
                    ? "Ordenar ascendente"
                    : "Ordenar descendente"
                }
                onClick={() => {
                  setOrderDirection((dir) => (dir === "asc" ? "desc" : "asc"));
                  setPage(0);
                }}
              >
                <FontAwesomeIcon
                  icon={
                    orderDirection === "desc"
                      ? "fa-solid fa-arrow-down-wide-short"
                      : "fa-solid fa-arrow-up-short-wide"
                  }
                />
              </button>
            </div>
          )}
        </div>
      )}

      {esMobile ? (
        <div className="mitabla__cards">
          {cargando && <p className="mitabla__estado-card">Cargando...</p>}

          {!cargando && rows.length === 0 && (
            <p className="mitabla__estado-card">{vacio}</p>
          )}

          {!cargando &&
            rows.map((row, idx) => (
              <article
                className={
                  onRowClick
                    ? "mitabla__card mitabla__row--clickable"
                    : "mitabla__card"
                }
                key={(row && row.id) || idx}
                onClick={onRowClick ? (e) => onRowClick(e, row) : undefined}
              >
                {(colTitulo || columnasAccion.length > 0) && (
                  <header className="mitabla__card-head">
                    {colTitulo && (
                      <div className="mitabla__card-titulo">
                        <span className="mitabla__card-etiqueta">
                          {colTitulo.title}
                        </span>
                        <strong>{celda(row, colTitulo)}</strong>
                      </div>
                    )}

                    {columnasAccion.length > 0 && (
                      <div className="mitabla__card-acciones">
                        {columnasAccion.map((col, i) => (
                          <React.Fragment key={col.field || "acc-" + i}>
                            {celda(row, col)}
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </header>
                )}

                {columnasDato.length > 0 && (
                  <dl className="mitabla__card-datos">
                    {columnasDato.map((col, i) => (
                      <div
                        className={
                          col.mobileBloque
                            ? "mitabla__card-dato mitabla__card-dato--bloque"
                            : "mitabla__card-dato"
                        }
                        key={col.field || "dato-" + i}
                      >
                        <dt>{col.title}</dt>
                        <dd>{celda(row, col)}</dd>
                      </div>
                    ))}
                  </dl>
                )}

                {detailPanel && (
                  <>
                    <button
                      type="button"
                      className="mitabla__card-toggle"
                      aria-expanded={!!expandidas[idx]}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDetalle(idx);
                      }}
                    >
                      <FontAwesomeIcon
                        icon={
                          expandidas[idx]
                            ? "fa-solid fa-chevron-up"
                            : "fa-solid fa-chevron-down"
                        }
                      />
                      {expandidas[idx] ? "Ocultar detalle" : "Ver detalle"}
                    </button>

                    {expandidas[idx] && (
                      <div className="mitabla__card-detalle">
                        {typeof detailPanel === "function"
                          ? detailPanel(row)
                          : detailPanel}
                      </div>
                    )}
                  </>
                )}
              </article>
            ))}
        </div>
      ) : (
        <div className="mitabla__scroll">
          <table className="mitabla__table">
            <thead>
              <tr>
                {detailPanel && <th className="mitabla__col-detalle" />}
                {columnasVisibles.map((col, i) => {
                  const ordenable =
                    sortingEnabled && col.sorting !== false && !!col.field;
                  const activo = orderBy === col.field && orderDirection;
                  return (
                    <th
                      key={col.field || "col-" + i}
                      style={col.headerStyle || col.cellStyle}
                      className={ordenable ? "mitabla__th--sortable" : undefined}
                      onClick={ordenable ? () => ordenarPor(col) : undefined}
                    >
                      {col.title}
                      {ordenable && (
                        <FontAwesomeIcon
                          className="mitabla__sort"
                          data-activo={activo ? "true" : "false"}
                          icon={
                            activo
                              ? orderDirection === "asc"
                                ? "fa-solid fa-arrow-up-short-wide"
                                : "fa-solid fa-arrow-down-wide-short"
                              : "fa-solid fa-sort"
                          }
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {cargando && (
                <tr className="mitabla__estado">
                  <td colSpan={colSpan}>Cargando...</td>
                </tr>
              )}

              {!cargando && rows.length === 0 && (
                <tr className="mitabla__estado">
                  <td colSpan={colSpan}>{vacio}</td>
                </tr>
              )}

              {!cargando &&
                rows.map((row, idx) => (
                  <React.Fragment key={(row && row.id) || idx}>
                    {/*
                      La franja sale del indice de la fila y no de un `:nth-child` de
                      CSS: la fila del detalle expandido es otro `<tr>` del mismo
                      tbody, asi que en cuanto se abre una, `nth-child` le corre el
                      turno a todas las de abajo y las franjas se dan vuelta solas.
                    */}
                    <tr
                      className={[
                        idx % 2 === 1 ? "mitabla__row--alterna" : null,
                        onRowClick ? "mitabla__row--clickable" : null,
                      ]
                        .filter(Boolean)
                        .join(" ") || undefined}
                      onClick={onRowClick ? (e) => onRowClick(e, row) : undefined}
                    >
                      {detailPanel && (
                        <td className="mitabla__col-detalle">
                          <button
                            type="button"
                            className="mitabla__toggle"
                            aria-expanded={!!expandidas[idx]}
                            aria-label={
                              expandidas[idx] ? "Ocultar detalle" : "Ver detalle"
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDetalle(idx);
                            }}
                          >
                            <FontAwesomeIcon
                              icon={
                                expandidas[idx]
                                  ? "fa-solid fa-chevron-down"
                                  : "fa-solid fa-chevron-right"
                              }
                            />
                          </button>
                        </td>
                      )}
                      {columnasVisibles.map((col, i) => (
                        <td key={col.field || "cel-" + i} style={col.cellStyle}>
                          {col.render ? col.render(row) : textoDe(row, col)}
                        </td>
                      ))}
                    </tr>
                    {detailPanel && expandidas[idx] && (
                      <tr className="mitabla__detalle">
                        <td colSpan={colSpan}>
                          {typeof detailPanel === "function"
                            ? detailPanel(row)
                            : detailPanel}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {paging && (
        <div className="mitabla__paginador">
          <label className="mitabla__pagesize">
            <span>{etiquetaFilas}</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>

          <span className="mitabla__rango">
            <strong>
              {desde}-{hasta}
            </strong>
            <span className="mitabla__rango-de">de</span>
            {totalCount}
          </span>

          <div className="mitabla__botones">
            <button
              type="button"
              aria-label="Primera página"
              disabled={page === 0}
              onClick={() => setPage(0)}
            >
              <FontAwesomeIcon icon="fa-solid fa-angles-left" />
            </button>
            <button
              type="button"
              aria-label="Página anterior"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              <FontAwesomeIcon icon="fa-solid fa-angle-left" />
            </button>

            <span className="mitabla__pagina" aria-live="polite">
              <strong>{page + 1}</strong>
              <span className="mitabla__pagina-sep">/</span>
              {pageCount}
            </span>

            <button
              type="button"
              aria-label="Página siguiente"
              disabled={page >= pageCount - 1}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            >
              <FontAwesomeIcon icon="fa-solid fa-angle-right" />
            </button>
            <button
              type="button"
              aria-label="Última página"
              disabled={page >= pageCount - 1}
              onClick={() => setPage(pageCount - 1)}
            >
              <FontAwesomeIcon icon="fa-solid fa-angles-right" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MiTabla;
